import Dialog from 'Components/Molecules/Dialog/Dialog';
import Button from 'Components/Atoms/Button/Button';
import OutlineAutomation from 'Components/Atoms/OutlineIcons/OutlineAutomation';
import usePlansAndPricingDialog from 'Hooks/usePlansAndPricingDialog';
import { useTranslation } from 'react-i18next';
import useProjectSelected from 'Hooks/useProjectSelected';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import useProjects from 'Hooks/useProjects';
import IconWarning from 'Components/Atoms/IconWarning/IconWarning';
import Menu, { MenuItem, MenuList } from 'Components/Molecules/Menu';
import DeleteAccountDialog from 'Components/Organisms/DeleteAccountDialog/DeleteAccountDialog';
import { useState } from 'react';
import useDebounceLastValue from 'Hooks/useDebounceLastValue';
import Icon from 'Components/Atoms/Icon/Icon';
import ChangeWorkspaceDialog from 'Components/Organisms/ChangeWorkspaceButton/ChangeWorkspaceDialog';
import useInvitations from 'Hooks/useInvitations';
import PendingInvitationAlert from 'Components/Organisms/PendingInvitationAlert/PendingInvitationAlert';
import PendingInvitationDialog from 'Components/Organisms/PendingInvitationDialog/PendingInvitationDialog';
import { cn } from 'Utils/cn';

export default function TrialExpiredDialog() {
    const { t } = useTranslation('common');
    const { openPlansAndPricing } = usePlansAndPricingDialog();
    const { isLoading: isProjectLoading, project } = useProjectSelected();
    const { projects, isLoading: isProjectsLoading } = useProjects();
    const { user, logout } = useUser();
    const [openDeleteAccountDialog, setOpenDeleteAccountDialog] = useState(false);
    const [openChangeProjectDialog, setOpenChangeProjectDialog] = useState(false);
    // Kept in state so the invitation dialog stays mounted while the invitations are refetched after answering it
    const [invitationToCheck, setInvitationToCheck] = useState(null);
    const { invitations } = useInvitations();
    const pendingInvitation = invitations[0];

    const hasMultipleProjects = !isProjectsLoading && projects.length > 1;
    const isClientUser = !!user && !user.is_support;

    const isTrialExpired = !isProjectLoading && !project.subscription && isClientUser && user.is_onboarded;
    // Former customers whose subscription ended (e.g. failed renewal) get a different copy than expired trials
    const isFormerCustomer = !!project.has_paid_before;

    // Opening is debounced, but closing is immediate so the dialog doesn't linger after switching to a paid project
    const openExpiredDialog = useDebounceLastValue(isTrialExpired, { delay: 1000, initialValue: false });

    if (openDeleteAccountDialog) {
        return <DeleteAccountDialog open={openDeleteAccountDialog} onClose={() => setOpenDeleteAccountDialog(false)} />;
    }

    if (openChangeProjectDialog) {
        return (
            <ChangeWorkspaceDialog open={openChangeProjectDialog} onClose={() => setOpenChangeProjectDialog(false)} />
        );
    }

    // Accepting moves the user to the invited project, so the expired dialog closes if that project is active
    if (invitationToCheck) {
        return (
            <PendingInvitationDialog invitation={invitationToCheck} open onClose={() => setInvitationToCheck(null)} />
        );
    }

    return (
        <Dialog open={isTrialExpired && openExpiredDialog} className="max-w-lg" keepSupportChat>
            <div className="pt-4 pb-9 relative">
                <div className="flex justify-center mb-4">
                    <IconWarning size={56} />
                </div>
                <div className="text-center text-xl sm:text-2xl font-semibold text-dark-blue mb-2">
                    {isFormerCustomer ? t('subscription-ended-title') : t('trial-expired-title')}
                </div>
                <div className="text-center sm:text-lg text-gray-500 text-pretty">
                    {isFormerCustomer ? t('subscription-ended-description') : t('trial-expired-description')}
                </div>

                <Menu
                    placement="bottom-start"
                    icon="gear"
                    iconSize={20}
                    iconColor="rgba(32,32,32,.5)"
                    className="absolute top-0 right-0"
                    zIndex={'var(--z-index-popover)'}
                >
                    <MenuList>
                        <MenuItem onClick={() => setOpenDeleteAccountDialog(true)}>{t('delete-account')}</MenuItem>
                        <MenuItem onClick={() => logout()}>{t('logout')}</MenuItem>
                    </MenuList>
                </Menu>
            </div>

            <div className="grid sm:grid-cols-2 gap-2 pb-4 sm:pb-6 max-w-sm mx-auto">
                <Button
                    variant="primary"
                    className={cn('pl-5', !hasMultipleProjects && 'sm:col-span-2')}
                    onClick={() => openPlansAndPricing()}
                >
                    <OutlineAutomation />
                    {t('trial-expired-purchase-plan')}
                </Button>
                {hasMultipleProjects && (
                    <Button className="gap-2" onClick={() => setOpenChangeProjectDialog(true)}>
                        <Icon name="switch" size={20} color="currentColor" />
                        {t('trial-expired-change-project')}
                    </Button>
                )}
            </div>

            {pendingInvitation && (
                <PendingInvitationAlert
                    invitation={pendingInvitation}
                    onCheck={() => setInvitationToCheck(pendingInvitation)}
                    className="rounded-lg mb-2"
                    stacked
                />
            )}
        </Dialog>
    );
}
